import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Collapse, Divider, Button, Tooltip, useTheme } from "@mui/material";
import KeyboardArrowDownIcon  from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import CheckIcon              from "@mui/icons-material/Check";
import CloseIcon              from "@mui/icons-material/Close";
import RemoveIcon             from "@mui/icons-material/Remove";
import MarkEmailUnreadIcon    from "@mui/icons-material/MarkEmailUnread";
import { MatrixRow_Type }     from "../api/axiosNetworkRequest/formAssignments";

// ─── Types ────────────────────────────────────────────────────────────────────
interface User {
  user_id: number;
  first_name: string;
  last_name: string;
  email_id: string;
}

type CellStatus = "not_assigned" | "submitted" | "waiting" | "overdue";

interface AcknowledgementMatrixViewProps {
  selectedFiles: Set<string>;
  selectedUserIds: Set<number>;
  users: User[];
  matrixRows: MatrixRow_Type[];
  onSendReminder: (assignment_id: number) => void;
  onEditMatrix: () => void;
  onExportMatrix: () => void;
}

// ─── Cell status logic ────────────────────────────────────────────────────────
function getCellStatus(row: MatrixRow_Type | undefined): CellStatus {
  if (!row) return "not_assigned";
  if (row.submitted) return "submitted";

  const lastAction = row.last_reminder_sent_at ?? row.assigned_at;
  const hoursSince = (Date.now() - new Date(lastAction).getTime()) / 36e5;


  return hoursSince < 24 ? "waiting" : "overdue";
}

// ─── Component ────────────────────────────────────────────────────────────────
const AcknowledgementMatrixView: React.FC<AcknowledgementMatrixViewProps> = ({
  selectedFiles,
  selectedUserIds,
  users,
  matrixRows,
  onSendReminder,
  onEditMatrix,
  onExportMatrix,
}) => {
  const assignmentMap = useMemo(() =>
    new Map(matrixRows.map(r => [`${r.file_name}||${r.user_id}`, r])),
    [matrixRows]
  );

  const tree = useMemo(() => {
    const t: Record<string, Record<string, string[]>> = {};
    selectedFiles.forEach(key => {
      const [lib, dept, file] = key.split("||");
      if (!t[lib]) t[lib] = {};
      if (!t[lib][dept]) t[lib][dept] = [];
      t[lib][dept].push(file);
    });
    return t;
  }, [selectedFiles]);

  const selectedUsers = useMemo(() =>
    users.filter(u => selectedUserIds.has(u.user_id)),
    [users, selectedUserIds]
  );

  const theme = useTheme();
  const [openLibs,  setOpenLibs]  = useState<Record<string, boolean>>({});
  const [openDepts, setOpenDepts] = useState<Record<string, boolean>>({});
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const toggleLib  = (lib: string)  => setOpenLibs(p  => ({ ...p, [lib]:  !p[lib]  }));
  const toggleDept = (key: string)  => setOpenDepts(p => ({ ...p, [key]:  !p[key]  }));

  const USER_COL_WIDTH = 110;
  const FILE_COL_WIDTH = 200;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* Header */}
      <Box sx={{ mx: 2, mt: 3, mb: 1.5, flexShrink: 0 }}>
        <Typography variant="h5" sx={{ px: 2, mb: 1.5, color: "text.primary", fontWeight: 700 }}>
          ACKNOWLEDGEMENT MATRIX
        </Typography>
        <Divider sx={{ borderColor: "divider", borderBottomWidth: 2, my: 1 }} />
      </Box>

      {/* Legend */}
      <Box sx={{ px: 4, pb: 1.5, display: "flex", gap: 3, flexShrink: 0 }}>
        {[
          { icon: <CheckIcon sx={{ fontSize: "0.95rem", color: "success.main" }} />,          label: "Submitted" },
          { icon: <MarkEmailUnreadIcon sx={{ fontSize: "0.95rem", color: "warning.main" }} />, label: "Awaiting (< 24 hrs)" },
          { icon: <CloseIcon sx={{ fontSize: "0.95rem", color: "error.main" }} />,             label: "Overdue — click to remind" },
          { icon: <RemoveIcon sx={{ fontSize: "0.95rem", color: "text.disabled" }} />,         label: "Not assigned" },
        ].map(({ icon, label }) => (
          <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {icon}
            <Typography variant="caption" sx={{ color: "text.secondary" }}>{label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "auto", px: 2, pb: 4 }}>
        {Object.entries(tree).map(([lib, depts]) => {
          const libOpen = !!openLibs[lib];
          return (
            <Box key={lib} sx={{ mb: 0.5, borderRadius: "4px", overflow: "hidden", border: "1px solid", borderColor: libOpen ? "primary.main" : "divider", transition: "border-color 0.2s" }}>

              {/* Library Header */}
              <Box
                onClick={() => toggleLib(lib)}
                sx={{
                  display: "flex", alignItems: "center", gap: 2,
                  px: 3, py: 2, minHeight: "72px",
                  bgcolor: libOpen ? "transparent" : theme.palette.background.paper,
                  background: libOpen ? theme.custom_gradient : undefined,
                  "&:hover": { bgcolor: libOpen ? undefined : "action.hover" },
                  cursor: "pointer",
                  transition: "background-color 0.15s ease, background 0.2s",
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 700, color: libOpen ? "common.white" : "text.primary" }}>{lib}</Typography>
                {libOpen
                  ? <KeyboardArrowUpIcon   sx={{ color: "common.white",   fontSize: "1.2rem" }} />
                  : <KeyboardArrowDownIcon sx={{ color: "text.secondary", fontSize: "1.2rem" }} />
                }
              </Box>

              {/* Departments */}
              <Collapse in={libOpen} timeout={200}>
                <Box sx={{ borderTop: "1px solid", borderColor: "divider", px: "10px", py: "8px", bgcolor: "background.paper" }}>
                  {Object.entries(depts).map(([dept, files]) => {
                    const deptKey  = `${lib}||${dept}`;
                    const deptOpen = !!openDepts[deptKey];
                    return (
                      <Box key={dept} sx={{ mb: "6px", borderRadius: "3px", overflow: "hidden" }}>

                        {/* Dept Header */}
                        <Box
                          onClick={() => toggleDept(deptKey)}
                          sx={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            px: 2, py: 1.75, bgcolor: "action.selected",
                            cursor: "pointer", "&:hover": { bgcolor: "action.focus" }, transition: "background-color 0.15s",
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 700, color: deptOpen ? "secondary.main" : "text.primary" }}>
                            {dept}
                          </Typography>
                          {deptOpen
                            ? <KeyboardArrowUpIcon   sx={{ color: "secondary.main", fontSize: "1.2rem" }} />
                            : <KeyboardArrowDownIcon sx={{ color: "text.secondary", fontSize: "1.2rem" }} />
                          }
                        </Box>

                        {/* Matrix Table */}
                        <Collapse in={deptOpen} timeout={180}>
                          <Box sx={{ bgcolor: "background.default", overflowX: "auto", px: 2, pb: 2 }}>

                            {/* Table Header */}
                            <Box sx={{ display: "grid", gridTemplateColumns: `${FILE_COL_WIDTH}px repeat(${selectedUsers.length}, ${USER_COL_WIDTH}px)`, position: "sticky", top: 0, bgcolor: "background.default", zIndex: 1 }}>
                              <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: "text.secondary" }}>Files</Typography>
                              </Box>
                              {selectedUsers.map(u => (
                                <Box key={u.user_id} sx={{ px: 1, py: 1.5, textAlign: "center" }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", fontSize: "0.75rem" }} noWrap>
                                    {u.first_name}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.7rem" }} noWrap>
                                    {u.last_name}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>

                            {/* Table Rows */}
                            {files.map((file, fi) => (
                              <Box
                                key={fi}
                                sx={{ display: "grid", gridTemplateColumns: `${FILE_COL_WIDTH}px repeat(${selectedUsers.length}, ${USER_COL_WIDTH}px)`, "&:hover": { bgcolor: "action.hover" } }}
                              >
                                <Box sx={{ px: 2, py: 1.2 }}>
                                  <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 500 }} noWrap>{file}</Typography>
                                </Box>

                                {selectedUsers.map(u => {
                                  const row    = assignmentMap.get(`${file}||${u.user_id}`);
                                  const status = getCellStatus(row);
                                  return (
                                    <Box
                                      key={u.user_id}
                                      sx={{
                                        py: 1.2, display: "flex", justifyContent: "center", alignItems: "center",
                                        transition: "background-color 0.12s",
                                        "&:hover": { bgcolor: "action.selected" },
                                      }}
                                    >
                                      {status === "not_assigned" && (
                                        <Tooltip title="Not assigned">
                                          <RemoveIcon sx={{ color: "text.disabled", fontSize: "1.1rem" }} />
                                        </Tooltip>
                                      )}
                                      {status === "submitted" && (
                                        <Tooltip title={row?.submitted_at ? `Submitted ${new Date(row.submitted_at).toLocaleDateString()}` : "Submitted"}>
                                          <CheckIcon sx={{ color: "success.main", fontSize: "1.1rem" }} />
                                        </Tooltip>
                                      )}
                                      {status === "waiting" && (
                                        <Tooltip title="Awaiting submission">
                                          <MarkEmailUnreadIcon sx={{ color: "warning.main", fontSize: "1.1rem" }} />
                                        </Tooltip>
                                      )}
                                      {status === "overdue" && (
                                        <Tooltip title="Overdue — click to send reminder">
                                          <CloseIcon
                                            onClick={() => row && onSendReminder(row.assignment_id)}
                                            sx={{ color: "error.main", fontSize: "1.1rem", cursor: "pointer" }}
                                          />
                                        </Tooltip>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      {/* Footer */}
      <Box sx={{ px: 3, py: 2, flexShrink: 0, display: "flex", justifyContent: "flex-end", gap: 2, borderTop: "1px solid", borderColor: "divider" }}>
        <Button
          variant="outlined"
          onClick={onExportMatrix}
          sx={{
            fontWeight: 600, px: 4, py: 1.5, borderRadius: "8px", fontSize: "0.85rem",
            borderColor: "primary.main", color: "primary.main",
            "&:hover": { borderColor: "primary.dark", color: "primary.dark" },
            minWidth: 180,
          }}
        >
          EXPORT MATRIX
        </Button>
        <Button
          variant="contained"
          onClick={onEditMatrix}
          sx={{
            fontWeight: 700, px: 4, py: 1.5, borderRadius: "8px", fontSize: "0.85rem",
            background: "#EF4444 !important",
            "&:hover": { background: "#c62828 !important" },
          }}
        >
          EDIT MATRIX
        </Button>
      </Box>

    </Box>
  );
};

export default AcknowledgementMatrixView;
